import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, MessageSquare, Mail, User, Mic, Play, Pause, Shield, 
  FileText, Clock, CheckCircle, Smartphone, Printer, 
  Megaphone, Users, Paperclip, MoreHorizontal, ArrowUpRight, ArrowDownLeft, Search
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';

const CommunicationSuiteSlide = () => {
  const [visibleEvents, setVisibleEvents] = useState([]);

  const events = [
    {
      id: 1,
      type: 'system',
      icon: Printer,
      text: 'Joseph (AI) received EOB Fax from MetLife • Uploaded to PMS',
      time: '8:45 AM'
    },
    {
      id: 2,
      type: 'system',
      icon: Shield,
      text: 'Amanda (AI) verified insurance: Active, $2000 rem.',
      time: '9:00 AM'
    },
    {
      id: 3,
      type: 'call-out',
      agent: 'Stephanie',
      role: 'Hygiene AI',
      time: '10:30 AM',
      duration: '1:24',
      transcript: "Hi Sarah, this is Stephanie from Asha Dental. I noticed you're due for your cleaning. Would you like to get that scheduled?",
      status: 'Completed'
    },
    {
      id: 4,
      type: 'sms-in',
      sender: 'Sarah',
      time: '10:32 AM',
      content: "I actually have a question about my bill from last time before I book.",
    },
    {
      id: 5,
      type: 'sms-out',
      sender: 'Jessica',
      role: 'Staff',
      time: '10:35 AM',
      content: "Hi Sarah! I can help with that. Looking at your ledger now - it looks like the co-pay for the filling. I can email you the details?",
    },
    {
      id: 6,
      type: 'sms-in',
      sender: 'Sarah',
      time: '10:36 AM',
      content: "Yes please email it. Also, can we just book the cleaning for next Tuesday afternoon?",
    },
    {
      id: 7,
      type: 'system',
      icon: CheckCircle,
      text: 'Appointment Booked: Hygiene • Tue 2:00 PM',
      time: '10:37 AM'
    }
  ];

  useEffect(() => {
    setVisibleEvents([]);
    let timeouts = [];
    
    // Start with some events visible immediately for context
    setVisibleEvents([events[0], events[1]]);

    // Animate the rest
    const remainingEvents = events.slice(2);
    remainingEvents.forEach((event, index) => {
      const timeout = setTimeout(() => {
        setVisibleEvents(prev => [...prev, event]);
      }, 1500 + (index * 2000)); 
      timeouts.push(timeout);
    });

    return () => timeouts.forEach(clearTimeout);
  }, []);

  return (
    <div className="h-full w-full bg-slate-900 flex items-center justify-center p-12 relative overflow-hidden text-white">
        {/* Abstract Background */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />

        <div className="flex flex-row h-full w-full max-w-[1600px] gap-12 relative z-10">
            {/* Left Side: Context */}
            <div className="w-2/5 flex flex-col justify-center space-y-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30 text-sm font-semibold w-fit">
                    <Users className="w-4 h-4" />
                    <span>Unified Platform</span>
                </div>
                
                <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
                    See the <span className="text-blue-400">entire patient story</span> in one place.
                </h2>
                
                <p className="text-lg text-slate-300 leading-relaxed">
                    Calls, texts, faxes, and internal notes—unified in a single timeline. Watch as AI agents handling insurance and recall work alongside your staff.
                </p>

                <div className="space-y-4 pt-4">
                    <div className="flex items-start gap-4">
                        <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400 mt-1">
                            <Phone className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-lg">Smart Voice Logs</h3>
                            <p className="text-slate-400 text-sm">Listen to call recordings and read AI-generated transcripts instantly.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-4">
                        <div className="p-2 bg-purple-500/20 rounded-lg text-purple-400 mt-1">
                            <MessageSquare className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-lg">Universal Inbox</h3>
                            <p className="text-slate-400 text-sm">Two-way texting for both your AI agents and your front desk staff.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-4">
                        <div className="p-2 bg-orange-500/20 rounded-lg text-orange-400 mt-1">
                            <FileText className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-lg">Automated Workflows</h3>
                            <p className="text-slate-400 text-sm">System events like "Fax Received" or "Insurance Verified" appear automatically.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Side: Timeline Interface */}
            <div className="w-3/5 h-full flex items-center">
                <motion.div 
                    initial={{ x: 40, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                    className="w-full h-[700px] bg-slate-50 rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col text-slate-900"
                >
                    {/* Header */}
                    <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0 z-20 shadow-sm">
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-xl">SM</div>
                                <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full"></div>
                            </div>
                            <div>
                                <h3 className="font-bold text-lg text-slate-900 leading-tight">Sarah Miller</h3>
                                <div className="flex items-center gap-2 text-sm text-slate-500">
                                    <span className="flex items-center gap-1"><Smartphone className="w-3 h-3" /> (555) 123-4567</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2">
                           <Button variant="ghost" size="icon" className="text-slate-400">
                                <Search className="w-5 h-5" />
                           </Button>
                           <Button variant="ghost" size="icon" className="text-slate-400">
                                <MoreHorizontal className="w-5 h-5" />
                           </Button>
                        </div>
                    </div>

                    {/* Timeline Area */}
                    <div className="flex-1 bg-slate-50 overflow-hidden flex flex-col relative">
                        <ScrollArea className="flex-1 px-6 py-6">
                            <div className="space-y-6 max-w-3xl mx-auto pb-4">
                                
                                <div className="flex items-center justify-center pb-2">
                                    <span className="text-xs font-semibold text-slate-400 bg-slate-100 px-3 py-1 rounded-full uppercase tracking-wider">Today</span>
                                </div>

                                <AnimatePresence mode="popLayout" initial={false}>
                                    {visibleEvents.map((event) => (
                                        <motion.div
                                            key={event.id}
                                            initial={{ opacity: 0, y: 10, scale: 0.98 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            layout
                                            transition={{ duration: 0.4, ease: "easeOut" }}
                                            className="w-full"
                                        >
                                            {/* System Event (Compact) */}
                                            {event.type === 'system' && (
                                                <div className="flex justify-center my-4">
                                                    <div className="flex items-center gap-2 px-4 py-1.5 bg-slate-100 rounded-full text-xs font-medium text-slate-500 border border-slate-200">
                                                        <event.icon className="w-3.5 h-3.5 text-slate-400" />
                                                        <span>{event.text}</span>
                                                        <span className="text-slate-300">•</span>
                                                        <span className="text-slate-400">{event.time}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Voice Call Log */}
                                            {event.type === 'call-out' && (
                                                <div className="flex justify-end mb-4">
                                                    <div className="flex flex-col items-end max-w-[85%]">
                                                        <div className="bg-white border border-slate-200 rounded-2xl rounded-tr-none p-4 shadow-sm w-full">
                                                            <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-3">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                                                                        <Phone className="w-4 h-4" />
                                                                    </div>
                                                                    <div>
                                                                        <div className="text-sm font-semibold text-slate-900">Outbound Call</div>
                                                                        <div className="text-xs text-slate-500">{event.agent} ({event.role}) • {event.duration}</div>
                                                                    </div>
                                                                </div>
                                                                <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full bg-slate-50 text-slate-600 hover:bg-slate-100">
                                                                    <Play className="w-3 h-3 fill-current" />
                                                                </Button>
                                                            </div>
                                                            <div className="space-y-1">
                                                                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Transcript Snippet</p>
                                                                <p className="text-sm text-slate-600 italic leading-relaxed">
                                                                    "{event.transcript}"
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <span className="text-[10px] text-slate-400 mt-1">{event.time}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* SMS Incoming */}
                                            {event.type === 'sms-in' && (
                                                <div className="flex justify-start mb-4">
                                                    <div className="flex items-end gap-2 max-w-[80%]">
                                                        <Avatar className="w-6 h-6 mb-1">
                                                            <AvatarFallback className="bg-slate-200 text-slate-600 text-[10px]">SM</AvatarFallback>
                                                        </Avatar>
                                                        <div className="flex flex-col items-start">
                                                            <div className="bg-white border border-slate-200 text-slate-800 rounded-2xl rounded-bl-none px-4 py-2.5 shadow-sm">
                                                                <p className="text-sm leading-relaxed">{event.content}</p>
                                                            </div>
                                                            <span className="text-[10px] text-slate-400 mt-1 ml-1">{event.time}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* SMS Outgoing */}
                                            {event.type === 'sms-out' && (
                                                <div className="flex justify-end mb-4">
                                                    <div className="flex flex-col items-end max-w-[80%]">
                                                        <div className="bg-blue-600 text-white rounded-2xl rounded-br-none px-4 py-2.5 shadow-md">
                                                            <p className="text-sm leading-relaxed">{event.content}</p>
                                                        </div>
                                                        <div className="flex items-center gap-1.5 mt-1 mr-1">
                                                            <span className="text-[10px] text-slate-400">{event.role} • {event.time}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                                <div className="h-4" /> {/* Spacer */}
                            </div>
                        </ScrollArea>
                        
                        {/* Input Area */}
                        <div className="p-4 bg-white border-t border-slate-200 z-10">
                            <div className="flex gap-2 items-end">
                                <div className="flex gap-1 pb-1">
                                    <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400">
                                        <Paperclip className="w-4 h-4" />
                                    </Button>
                                </div>
                                <div className="flex-1 bg-slate-100 rounded-2xl px-4 py-2.5 min-h-[44px] border border-transparent focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                                    <input 
                                        type="text" 
                                        placeholder="Message Sarah..." 
                                        className="w-full bg-transparent border-0 text-sm focus:outline-none placeholder:text-slate-400"
                                    />
                                </div>
                                <Button size="icon" className="rounded-full w-10 h-10 bg-blue-600 hover:bg-blue-700 shadow-md">
                                    <ArrowUpRight className="w-5 h-5" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    </div>
  );
};

export default CommunicationSuiteSlide;